import { useRef, useState } from 'react';

import type { UsePaginationArgs, UsePaginationCallbackArgs } from './types';

const usePagination = <T>({ callback }: UsePaginationArgs<T>) => {
    const nextPageToken = useRef<string | undefined>(undefined);
    const [data, setData] = useState<T[] | undefined>(undefined);
    const [totalCount, setTotalCount] = useState<number>(0);
    const isReset = useRef(false);

    const reset = () => {
        nextPageToken.current = undefined;
        setData(undefined);
        isReset.current = true;
    };

    async function* fetch(args: UsePaginationCallbackArgs) {
        const { pageToken } = args || {};

        do {
            if (!nextPageToken.current && data !== undefined && !isReset.current) {
                return;
            }

            isReset.current = false;

            const response = await callback({
                ...args,
                pageToken: nextPageToken.current || pageToken
            });

            setTotalCount(response?.totalCount ?? 0);

            if (response && response.objects) {
                nextPageToken.current = response.nextPageToken;

                setData((currentData = []) => [...currentData, ...response.objects]);

                if (!response.nextPageToken) {
                    return response.objects;
                }

                yield response.objects;
            } else {
                nextPageToken.current = undefined;
            }
        } while (typeof nextPageToken.current !== 'undefined');
    }

    return {
        data,
        fetch,
        reset,
        totalCount
    };
};

export default usePagination;
